import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Sparkles, ArrowDown } from "lucide-react";

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] pt-28 pb-20 overflow-hidden grain">
      {/* backdrop */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="aurora bg-[#C9A227] w-[520px] h-[520px] -top-40 -left-40" style={{opacity:0.25}} />
        <div className="aurora bg-[#047857] w-[560px] h-[560px] top-40 -right-40" style={{opacity:0.25}} />
        <div className="aurora bg-[#10B981] w-[380px] h-[380px] bottom-0 left-1/3" style={{opacity:0.18}} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black" />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease:[0.16,1,0.3,1]}}
          className="flex items-center gap-3 mb-8"
        >
          <span className="eyebrow"><Sparkles size={12} /> DPIIT Recognized Startup · Est. 2019</span>
          <span className="hidden md:inline eyebrow-emerald">AI-Powered Growth Ecosystem</span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease:[0.16,1,0.3,1]}}
              className="text-[44px] leading-[1.02] sm:text-6xl lg:text-[86px] font-semibold tracking-tight"
            >
              Marketing<br />
              <span className="text-gold-gradient">that&nbsp;actually</span>
              <span className="text-emerald-gradient"> works.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25, ease:[0.16,1,0.3,1]}}
              className="mt-8 text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              SYR Mediaworks is an AI-powered performance marketing & technology ecosystem —
              turning brands, publishers and enterprises into industry leaders through data,
              creativity and measurable growth since 2019.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease:[0.16,1,0.3,1]}}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a href="#about" className="btn-gold">
                Explore Company <ArrowUpRight size={18} />
              </a>
              <a href="https://wa.me/917610512615" target="_blank" rel="noreferrer" className="btn-ghost">
                <MessageCircle size={18} /> Start Partnership
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6, ease:[0.16,1,0.3,1]}}
              className="mt-14 flex items-center gap-4 text-xs uppercase tracking-[0.28em] text-white/40"
            >
              <span>Trusted by</span>
              <div className="h-px flex-1 max-w-40 bg-gradient-to-r from-white/30 to-transparent" />
              <span className="text-white/70">Angel One · ACKO · Airtel · Upstox · Muthoot · Dream11</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease:[0.16,1,0.3,1]}}
            className="lg:col-span-4"
          >
            <div className="relative float-y">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#C9A227]/25 to-[#047857]/25 blur-3xl rounded-full" />
              <div className="relative glass rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#F4D983]">Live Board</div>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="w-2 h-2 rounded-full bg-yellow-400" />
                    <span className="w-2 h-2 rounded-full bg-green-400" />
                  </div>
                </div>
                <div className="mt-5 space-y-4">
                  {[
                    { k: "App Installs", v: "5.1M+", c: "text-[#F4D983]" },
                    { k: "Registrations", v: "1.24M+", c: "text-[#6EE7B7]" },
                    { k: "Demat Accounts", v: "165K+", c: "text-[#F4D983]" },
                    { k: "Traded Accounts", v: "40K+", c: "text-[#6EE7B7]" },
                  ].map((r) => (
                    <div key={r.k} className="flex items-center justify-between border-b border-white/5 pb-3">
                      <span className="text-sm text-white/60">{r.k}</span>
                      <span className={`text-xl font-semibold ${r.c}`}>{r.v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 h-1.5 rounded-full overflow-hidden bg-white/5">
                  <div className="h-full w-2/3 bg-gradient-to-r from-[#C9A227] to-[#10B981]" />
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-white/40">
                  <span>ROI Delivered</span>
                  <span>Q4 2025</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-16 flex items-center gap-3 text-white/40 text-xs uppercase tracking-[0.3em]">
          <ArrowDown size={14} className="animate-bounce" /> Scroll to explore
        </div>
      </div>
    </section>
  );
}
